import { useDeferredValue } from 'react';
import type { DefaultError } from '@tanstack/query-core';
import { UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchCampaignAuction } from 'api/campaigns';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Auction } from 'types/domain';

type AuctionQueryOptions<TData> = Omit<
    UseSuspenseQueryOptions<TData, DefaultError, TData, any>,
    'queryKey' | 'queryFn'
>;

type PossibleAuctionOptions = AuctionQueryOptions<Auction | null>;
type AuctionOptions = AuctionQueryOptions<Auction>;

const useAuctionIds = () => {
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;
    const auctionId = useDeferredValue(campaign.auction_id);
    return { campaignId, auctionId };
};

const useCurrentPossibleAuction = (options: PossibleAuctionOptions = {}) => {
    const { campaignId, auctionId } = useAuctionIds();

    return useSuspenseQuery({
        queryKey: campaignQueryKeys.auction(campaignId, auctionId as number),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchCampaignAuction({ campaignId, auctionId: auctionId as number }),
                typeof auctionId === 'number',
            ),
        ...options,
    });
};

const useCurrentAuction = (options: AuctionOptions = {}) => {
    const { campaignId, auctionId } = useAuctionIds();

    if (typeof auctionId !== 'number') {
        throw new Error('Auction ID is not available');
    }

    return useSuspenseQuery({
        queryKey: campaignQueryKeys.auction(campaignId, auctionId),
        queryFn: () => fetchCampaignAuction({ campaignId, auctionId }),
        ...options,
    });
};

export { useCurrentAuction, useCurrentPossibleAuction };
